import { useMarketDataSelection } from "../../shared/api/market-data-selection.js";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";

import { Button } from "../../shared/ui/button.js";
import {
  allIntervals,
  defaultBacktestFormValues,
  intervalLabels,
  type BacktestFormValues,
} from "./backtest-form.js";
import { useLaunchFilteredBacktests } from "./filtered-backtest-queries.js";
import type { BotView } from "./models.js";

interface FilteredBacktestFormValues extends BacktestFormValues {
  readonly confirmed: boolean;
}

export function FilteredBacktestLauncher({ bots }: { readonly bots: readonly BotView[] }) {
  const [open, setOpen] = useState(false);
  const selection = useMarketDataSelection();
  const dxlink = selection.data?.backtestProvider === "dxlink";
  const launch = useLaunchFilteredBacktests();
  const eligibleBots = bots.filter((bot) => bot.state === "ready");
  const skippedCount = bots.length - eligibleBots.length;
  const form = useForm<FilteredBacktestFormValues>({
    defaultValues: { ...defaultBacktestFormValues(), confirmed: false },
    mode: "onChange",
  });
  useEffect(() => {
    if (dxlink) form.setValue("adjustment", "raw");
  }, [dxlink, form]);
  const submit = form.handleSubmit(async (values) => {
    await launch.mutateAsync({
      bots: eligibleBots,
      values: dxlink ? { ...values, adjustment: "raw" } : values,
    });
  });
  const launchedCount = launch.data?.filter((outcome) => outcome.status === "launched").length ?? 0;
  const failed = launch.data?.filter((outcome) => outcome.status === "failed") ?? [];
  const botNoun = eligibleBots.length === 1 ? "bot" : "bots";

  return (
    <div className="filtered-backtest-launcher">
      <Button
        disabled={bots.length === 0 || launch.isPending}
        onClick={() => {
          launch.reset();
          setOpen((current) => !current);
        }}
        tone="quiet"
      >
        {open ? "Close bulk launcher" : "Run filtered backtests"}
      </Button>
      {open ? (
        <form
          aria-label="Run backtests for filtered bots"
          className="form-stack filtered-backtest-form"
          onSubmit={(event) => void submit(event)}
        >
          <div>
            <p className="eyebrow">Research only</p>
            <h2>Run filtered backtests</h2>
            <p className="section-description">
              Launch one backtest for each of the {eligibleBots.length} ready {botNoun} currently
              shown.
              {skippedCount === 0
                ? ""
                : ` ${String(skippedCount)} non-ready ${skippedCount === 1 ? "bot is" : "bots are"} excluded.`}
            </p>
          </div>
          <p className="field-hint">
            Data provider:{" "}
            {selection.isPending
              ? "Loading�"
              : selection.isError
                ? "Unavailable � reload to try again"
                : dxlink
                  ? "tastytrade DXLink � native candles; limited intraday history including warm-up."
                  : "Alpaca"}
          </p>
          <div className="field-grid">
            <div className="field-stack">
              <label htmlFor="filtered-backtest-from">From</label>
              <input
                id="filtered-backtest-from"
                type="date"
                {...form.register("from", { required: true })}
              />
            </div>
            <div className="field-stack">
              <label htmlFor="filtered-backtest-to">To</label>
              <input
                id="filtered-backtest-to"
                type="date"
                {...form.register("to", { required: true })}
              />
            </div>
            <div className="field-stack">
              <label htmlFor="filtered-backtest-interval">Default bar interval</label>
              <select id="filtered-backtest-interval" {...form.register("interval")}>
                {allIntervals.map((interval) => (
                  <option key={interval} value={interval}>
                    {intervalLabels[interval]}
                  </option>
                ))}
              </select>
              <span className="field-hint">
                A strategy that requires a specific source interval uses that interval instead.
                Monthly trend/cash preserves your selected 1-minute, 5-minute, 15-minute or daily
                interval; 1-hour requests are rejected, not replaced. It requires raw prices and
                next-session-open fills. Shared cost inputs below apply to every bot; set a $1
                minimum commission for its study costs.
              </span>
            </div>
            <div className="field-stack">
              <label htmlFor="filtered-backtest-adjustment">Price adjustment</label>
              <select id="filtered-backtest-adjustment" {...form.register("adjustment")}>
                {dxlink ? null : <option value="all">Splits and dividends</option>}
                {dxlink ? null : <option value="split">Splits only</option>}
                <option value="raw">{dxlink ? "Native DXLink candles" : "Raw"}</option>
              </select>
            </div>
            <div className="field-stack">
              <label htmlFor="filtered-initial-cash">Initial cash, USD</label>
              <input
                id="filtered-initial-cash"
                inputMode="decimal"
                {...form.register("initialCashUsd", { required: true })}
              />
            </div>
            <div className="field-stack">
              <label htmlFor="filtered-fill-timing">Fill timing</label>
              <select id="filtered-fill-timing" {...form.register("fillTiming")}>
                <option value="next-bar-open">Next bar open</option>
                <option value="bar-close">Bar close</option>
              </select>
            </div>
            <div className="field-stack">
              <label htmlFor="filtered-slippage">Slippage, basis points</label>
              <input
                id="filtered-slippage"
                min="0"
                type="number"
                {...form.register("slippageBasisPoints", { valueAsNumber: true })}
              />
            </div>
            <div className="field-stack">
              <label htmlFor="filtered-commission">Commission per share, USD</label>
              <input
                id="filtered-commission"
                inputMode="decimal"
                {...form.register("commissionPerShareUsd", { required: true })}
              />
            </div>
          </div>
          <div className="field-stack">
            <label htmlFor="filtered-minimum-commission">Minimum commission per order, USD</label>
            <input
              id="filtered-minimum-commission"
              inputMode="decimal"
              placeholder="0"
              {...form.register("minimumCommissionUsd")}
            />
          </div>
          <label className="check-field">
            <input type="checkbox" {...form.register("confirmed", { required: true })} />
            <span>
              <strong>
                Launch {eligibleBots.length} backtest {eligibleBots.length === 1 ? "job" : "jobs"}
              </strong>
              <small>
                Each job freezes its bot's active revision and cannot place broker orders.
              </small>
            </span>
          </label>
          {launch.error === null ? null : (
            <p className="form-error" role="alert">
              The bulk launcher stopped unexpectedly. No additional jobs will be submitted.
            </p>
          )}
          {launch.data === undefined ? null : (
            <div className="bulk-launch-result" role="status">
              <strong>
                Launched {launchedCount} of {launch.data.length} backtests.
              </strong>
              {failed.length === 0 ? null : (
                <p>
                  Failed: {failed.map((outcome) => outcome.botName).join(", ")}. Open each bot to
                  review its current state and retry.
                </p>
              )}
              {failed
                .filter((outcome) => outcome.reason !== undefined)
                .map((outcome) => (
                  <p key={outcome.botId}>
                    {outcome.botName}: {outcome.reason}
                  </p>
                ))}
            </div>
          )}
          <div className="button-row">
            <Button
              disabled={
                selection.data === undefined ||
                selection.isError ||
                eligibleBots.length === 0 ||
                launch.isPending ||
                !form.formState.isValid
              }
              type="submit"
            >
              {launch.isPending
                ? "Launching…"
                : `Launch ${String(eligibleBots.length)} ${eligibleBots.length === 1 ? "backtest" : "backtests"}`}
            </Button>
          </div>
        </form>
      ) : null}
    </div>
  );
}
